import { classNames } from 'primereact/utils';
import { CSSProperties, JSX, memo } from 'react';
import { DragPreviewProps } from 'react-arborist';
import { XYCoord } from 'react-dnd';
import { SectionsTreeNode } from '../types';
import { TREE_ROW_HEIGHT } from '../constants';
import { SectionsTreeNodeContent } from './SectionsTreeNodeContent';

const layerStyles: CSSProperties = {
  position: 'fixed',
  pointerEvents: 'none',
  zIndex: 100,
  left: 0,
  top: 0,
  width: '100%',
  height: '100%',
};

const getStyle = (offset: XYCoord | null) => {
  if (!offset) return { display: 'none' };
  const { x, y } = offset;
  return { transform: `translate(${x}px, ${y}px)` };
};

const Overlay = memo(function Overlay(props: { children: JSX.Element | JSX.Element[]; isDragging: boolean }) {
  if (!props.isDragging) return null;
  return <div style={layerStyles}>{props.children}</div>;
});

function Position(props: { children: JSX.Element; offset: XYCoord | null }) {
  return (
    <div className="row preview" style={getStyle(props.offset)}>
      {props.children}
    </div>
  );
}

// Stack of up to 3 preview cards; the pill shows the total count.
const MAX_VISIBLE_STACK = 3;
const STACK_OFFSET_PX = 4;

const PreviewCard = ({ data }: { data: SectionsTreeNode }) => (
  <div
    className={classNames(
      'flex w-80 items-center gap-2 pr-[12px] pl-[24px]',
      'border-fletchers-gray-110 rounded-[6px] border-1',
      'bg-fletchers-gray-50 shadow-[0_4px_12px_rgba(0,0,0,0.12)]',
    )}
    style={{ height: TREE_ROW_HEIGHT }}
  >
    <SectionsTreeNodeContent data={data} />
  </div>
);

const PreviewNode = memo((props: { labels: SectionsTreeNode[] | null }) => {
  if (!props.labels || !props.labels.length) return null;
  const stack = props.labels.slice(0, MAX_VISIBLE_STACK);
  return (
    <div className="relative w-80">
      {/* Render deepest card first so later cards paint on top. */}
      {/* Deepest first so the top card paints last. */}
      {stack.map((label, index) => {
        const depth = stack.length - 1 - index;
        const isTop = depth === 0;
        return (
          <div
            key={label.id}
            className={isTop ? 'relative' : 'absolute top-0 left-0'}
            style={
              isTop ? undefined : { transform: `translate(${depth * STACK_OFFSET_PX}px, ${depth * STACK_OFFSET_PX}px)` }
            }
          >
            <PreviewCard data={label} />
          </div>
        );
      })}
      <span className="bg-fletchers-green-500 absolute -top-3 left-1/2 -translate-x-1/2 rounded-[4px] px-2 py-0.5 text-[0.75rem] font-[400] whitespace-nowrap text-white">
        Moving {props.labels.length} {props.labels.length === 1 ? 'item' : 'items'}
      </span>
    </div>
  );
});

interface BundleSectionsTreeDragPreviewProps extends DragPreviewProps {
  labels: SectionsTreeNode[] | null;
}

export const BundleSectionsTreeDragPreview = ({ offset, isDragging, labels }: BundleSectionsTreeDragPreviewProps) => {
  return (
    <Overlay isDragging={isDragging}>
      <Position offset={offset}>
        <PreviewNode labels={labels} />
      </Position>
    </Overlay>
  );
};
